import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Users,
  Activity,
  Clock,
  Battery,
  Bell,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2
} from 'lucide-react';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  averageEnergy: number;
  alertsToday: number;
  energyData: any[];
  userTypes: any[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    averageEnergy: 0,
    alertsToday: 0,
    energyData: [],
    userTypes: []
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadStats();
    // Mettre à jour les stats toutes les 5 minutes
    const interval = setInterval(loadStats, 300000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      // Simuler le chargement des données depuis Supabase
      // Dans une implémentation réelle, ces données viendraient de la base de données
      
      const mockData = {
        totalUsers: 156,
        activeUsers: 89,
        averageEnergy: 75,
        alertsToday: 12,
        energyData: [
          { time: '00:00', value: 65 },
          { time: '04:00', value: 45 },
          { time: '08:00', value: 78 },
          { time: '12:00', value: 82 },
          { time: '16:00', value: 71 },
          { time: '20:00', value: 68 }
        ],
        userTypes: [
          { name: 'Projector', value: 45 },
          { name: 'Generator', value: 35 },
          { name: 'Manifestor', value: 15 },
          { name: 'Reflector', value: 5 }
        ]
      };

      setStats(mockData);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les statistiques.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="hover:shadow-lg transition-all">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Utilisateurs</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">{stats.totalUsers}</p>
                  <span className="text-sm text-green-600">
                    {stats.activeUsers} actifs
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-all">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Battery className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Énergie moyenne</p>
                <p className="text-2xl font-bold">{stats.averageEnergy}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-all">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Bell className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Alertes aujourd'hui</p>
                <p className="text-2xl font-bold">{stats.alertsToday}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-all">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Taux d'activité</p>
                <p className="text-2xl font-bold">
                  {Math.round((stats.activeUsers / stats.totalUsers) * 100)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="hover:shadow-lg transition-all">
          <CardHeader>
            <h3 className="text-lg font-semibold">Évolution de l'énergie</h3>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.energyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#1E40AF"
                    strokeWidth={2}
                    dot={{ fill: '#1E40AF', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-all">
          <CardHeader>
            <h3 className="text-lg font-semibold">Distribution des types</h3>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.userTypes}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {stats.userTypes.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alertes récentes */}
      <Card className="hover:shadow-lg transition-all">
        <CardHeader>
          <h3 className="text-lg font-semibold">Alertes récentes</h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="font-medium">Baisse d'énergie critique</p>
                  <p className="text-sm text-gray-600">3 utilisateurs affectés</p>
                </div>
              </div>
              <Button variant="outline" size="sm">
                Voir les détails
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="font-medium">Cycle de sommeil perturbé</p>
                  <p className="text-sm text-gray-600">5 utilisateurs affectés</p>
                </div>
              </div>
              <Button variant="outline" size="sm">
                Voir les détails
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium">Synchronisation réussie</p>
                  <p className="text-sm text-gray-600">15 nouveaux appareils</p>
                </div>
              </div>
              <Button variant="outline" size="sm">
                Voir les détails
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}